// The Home HYDROS tile's note when a key-connected controller reads nothing
// for some parameters until the keeper picks this tank's probes (owner,
// 2026-10-01), and the probe values the tile and the Equipment panel show.
// Pure, so the wording is tested.

import { fToC } from './units.js';
import { pptToSg } from './salinity.js';

const NAMES = {
  temp: 'Temperature',
  ph: 'pH',
  salinity: 'Salinity',
  alk: 'Alkalinity',
  orp: 'ORP',
};

const andList = (xs) => (xs.length > 1 ? `${xs.slice(0, -1).join(', ')} and ${xs.at(-1)}` : xs[0]);

// Picks naming a probe the controller has logged nothing for lately
// (unplugged, failed, or renamed in HYDROS) while its other probes still
// log. The tank reads nothing for that parameter rather than another tank's
// probe (nightly sweep 5), so the keeper is told so and offered the pick
// again. A controller that logs nothing at all (offline, a power cut) has no
// missing pick: a re-pick then could only land on the wrong probe or on
// "Not in this tank", which would stay after the power came back.
export function missingPicks(link) {
  const choice = link?.choice || {};
  const series = link?.series || {};
  if (!Object.values(series).some((names) => Array.isArray(names) && names.length)) return [];
  return Object.keys(choice).filter(
    (k) => typeof choice[k] === 'string' && !(series[k] || []).includes(choice[k])
  );
}

// The parameters the Equipment panel asks about: two or more probes of a
// kind, any probe on a controller that feeds another tank, or a pick that
// went missing.
export function pickRows(link) {
  const series = link?.series || {};
  const shared = Array.isArray(link?.shared_with) && link.shared_with.length > 0;
  const missing = new Set(missingPicks(link));
  return Object.keys(NAMES).filter(
    (p) => (series[p] || []).length > 1 || (shared && (series[p] || []).length) || missing.has(p)
  );
}

// null when nothing needs picking; else { lead, rest, canPick }. A param the
// keeper hid on this tank (alk on a new QT) is left out: they chose not to
// track it, so "not read yet" would nag about nothing.
export function probePickMessage(link, tankName, params = null) {
  const needs = (Array.isArray(link?.needs_choice) ? link.needs_choice : []).filter(
    (k) => params?.[k]?.hidden !== true
  );
  if (!link?.via_key || needs.length === 0) return null;
  const names = needs.map((k, i) => {
    const n = NAMES[k] || k;
    return i === 0 || n === 'pH' || n === 'ORP' ? n : n.toLowerCase();
  });
  const canPick = Boolean(link.can_edit);
  const missing = new Set(missingPicks(link));
  const gone = needs.filter((k) => missing.has(k));
  const other = needs.filter((k) => !missing.has(k));
  const parts = [];
  if (gone.length) {
    const one = gone.length === 1;
    parts.push(
      `${andList(gone.map((k) => `"${link.choice[k]}"`))}, the probe${one ? '' : 's'} picked for ${tankName}, ${
        one ? 'has' : 'have'
      } sent no readings lately. Check ${one ? 'it' : 'them'}${
        canPick ? `, or pick again if ${one ? 'it was' : 'they were'} renamed in HYDROS.` : '.'
      }`
    );
  }
  if (other.length) {
    const shared = Array.isArray(link.shared_with) && link.shared_with.length > 0;
    const which = gone.length
      ? andList(
          other.map((k) =>
            NAMES[k] === 'pH' || NAMES[k] === 'ORP' ? NAMES[k] : (NAMES[k] || k).toLowerCase()
          )
        )
      : other.length > 1
        ? 'each'
        : 'it';
    const why = shared
      ? `This HYDROS controller also feeds ${andList(link.shared_with)}`
      : `This HYDROS controller has more than one probe for ${which}`;
    parts.push(
      `${why}, so ${
        canPick
          ? `pick which probes are ${tankName}'s, or "Not in this tank" for one it doesn't have.`
          : "the tank's owner picks which probes this tank reads."
      }`
    );
  }
  return { lead: `${andList(names)} not read yet.`, rest: parts.join(' '), canPick };
}

// A live HYDROS probe value in the keeper's own units, as { num, unit }.
// The route sends temperature in °F and salinity in ppt; a keeper on °C or
// on S.G. must not see those (the Home tile once showed "35.0 ppt" beside
// cards reading 1.026). A value with no storage unit attached is taken to
// be in storage units. Anything else (ppm TDS, mV, dKH) keeps its own unit;
// pH has none, the label already says pH.
export function probeReading(v, { tempUnit = 'F', salinityUnit = 'ppt' } = {}) {
  const n = Number(v?.value);
  const unit = typeof v?.unit === 'string' ? v.unit.trim() : '';
  if (!Number.isFinite(n)) return { num: String(v?.value ?? ''), unit };
  if (v.key === 'temp' && (!unit || unit === '°F')) {
    return tempUnit === 'C'
      ? { num: fToC(n).toFixed(1), unit: '°C' }
      : { num: n.toFixed(1), unit: '°F' };
  }
  if (v.key === 'salinity' && (!unit || unit === 'ppt')) {
    if (salinityUnit === 'sg') return { num: pptToSg(n, 3).toFixed(3), unit: 'S.G.' };
    return { num: n.toFixed(1), unit: salinityUnit === 'psu' ? 'PSU' : 'ppt' };
  }
  if (v.key === 'ph') return { num: n.toFixed(2), unit: '' };
  if (v.key === 'orp') return { num: String(Math.round(n)), unit: unit || 'mV' };
  return { num: n.toFixed(1), unit };
}

export function probeReadingText(v, opts) {
  const r = probeReading(v, opts);
  return r.unit ? `${r.num} ${r.unit}` : r.num;
}

const PROBE_NAMES = { ...NAMES, level: 'Water level', leak: 'Leak' };

export function probeName(key) {
  return PROBE_NAMES[key] || key;
}

// Short labels for the Home tile. Two probes of one kind (a password-linked
// controller with a display and a sump temperature probe) are numbered, so
// the tile never shows two identical "Temp" cells; the mapper sends no probe
// name to tell them apart by.
const SHORT = { temp: 'Temp', alk: 'Alk' };
export function tileLabels(values) {
  const list = Array.isArray(values) ? values : [];
  const total = {};
  for (const v of list) total[v?.key] = (total[v?.key] || 0) + 1;
  const seen = {};
  return list.map((v) => {
    const base = SHORT[v?.key] || probeName(v?.key);
    if (total[v?.key] < 2) return base;
    seen[v.key] = (seen[v.key] || 0) + 1;
    return `${base} ${seen[v.key]}`;
  });
}
